'use client';

import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

export interface Step {
  id: string;
  title: string;
  description?: string;
  status: 'complete' | 'current' | 'upcoming';
}

interface StepsProps {
  steps: Step[];
  className?: string;
}

export function Steps({ steps, className }: StepsProps) {
  return (
    <nav aria-label="Progress" className={cn('mx-auto w-full max-w-2xl', className)}>
      <ol className="relative flex items-start justify-center">
        {/* Background progress line */}
        <div className="bg-border absolute top-6 right-6 left-6 -z-10 h-0.5" />

        {/* Active progress line */}
        <div
          className="absolute top-6 left-6 -z-10 h-0.5 bg-gradient-to-r from-amber-400 to-amber-300 transition-all duration-500 ease-out"
          style={{
            width: `${Math.max(0, (steps.findIndex((s) => s.status === 'current') / Math.max(1, steps.length - 1)) * 100)}%`,
          }}
        />

        {steps.map((step, stepIdx) => (
          <li key={step.id} className="relative flex flex-1 flex-col items-center">
            {/* Step circle */}
            <div className="relative z-10 mb-4">
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full border-4 transition-all duration-300 ease-out',
                  'shadow-lg backdrop-blur-sm',
                  step.status === 'complete' &&
                    'scale-110 border-amber-400 bg-amber-400 text-gray-900 shadow-amber-400/25',
                  step.status === 'current' &&
                    'bg-card scale-110 border-amber-400 text-amber-400 ring-4 shadow-amber-400/30 ring-amber-400/20',
                  step.status === 'upcoming' &&
                    'border-border bg-card text-muted hover:border-amber-400/30 hover:shadow-md',
                )}
              >
                {step.status === 'complete' ? (
                  <CheckIcon className="h-5 w-5 font-bold" />
                ) : (
                  <span className="text-sm font-bold tracking-tight">{stepIdx + 1}</span>
                )}
              </div>

              {/* Animated pulse for current step */}
              {step.status === 'current' && (
                <div className="absolute inset-0 animate-pulse rounded-full border-2 border-amber-400 opacity-30" />
              )}
            </div>

            {/* Step content */}
            <div className="space-y-1 px-2 text-center">
              <h3
                className={cn(
                  'text-sm font-semibold tracking-tight transition-colors duration-200',
                  step.status === 'current' && 'text-amber-600 dark:text-amber-400',
                  step.status === 'complete' && 'text-foreground',
                  step.status === 'upcoming' && 'text-muted',
                )}
              >
                {step.title}
              </h3>
              {step.description && (
                <p
                  className={cn(
                    'text-xs leading-relaxed transition-colors duration-200',
                    step.status === 'current' && 'text-muted',
                    step.status === 'complete' && 'text-muted',
                    step.status === 'upcoming' && 'text-muted',
                  )}
                >
                  {step.description}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// Compact horizontal version
export function StepIndicator({ steps, className }: StepsProps) {
  return (
    <div className={cn('p-2 sm:p-3 md:p-4', className)}>
      {/* Mobile: Horizontal scrollable compact - numbers/dots only */}
      <div className="md:hidden">
        <div className="scrollbar-hide flex items-center gap-1.5 overflow-x-auto overflow-y-visible pt-2 pb-1 sm:gap-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex min-w-[2rem] flex-shrink-0 flex-col items-center gap-0.5 sm:min-w-[2.5rem]">
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ease-out sm:h-7 sm:w-7',
                    'shadow-md backdrop-blur-sm',
                    step.status === 'complete' && 'bg-amber-400 text-gray-900 shadow-amber-400/25',
                    step.status === 'current' &&
                      'scale-110 border-2 border-amber-400 bg-amber-50 text-amber-600 ring-2 shadow-amber-400/20 ring-amber-400/10 dark:bg-amber-950 dark:text-amber-400',
                    step.status === 'upcoming' &&
                      'border-border bg-card text-muted border hover:border-amber-400/30',
                  )}
                  title={step.title}
                >
                  {step.status === 'complete' ? (
                    <CheckIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  ) : (
                    index + 1
                  )}
                </div>
                {/* Show only current step title on mobile */}
                {step.status === 'current' && (
                  <span className="text-center text-xs font-medium whitespace-nowrap text-amber-600 dark:text-amber-400">
                    {step.title}
                  </span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'h-px w-3 flex-shrink-0 transition-all duration-500 ease-out sm:w-4',
                    step.status === 'complete'
                      ? 'bg-gradient-to-r from-amber-400 to-amber-300'
                      : 'bg-border',
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Tablet and Desktop: Horizontal layout with labels */}
      <div className="hidden items-center justify-center gap-2 md:flex md:gap-3 lg:gap-4">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2 md:gap-3">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ease-out',
                    'shadow-md backdrop-blur-sm',
                    step.status === 'complete' && 'bg-amber-400 text-gray-900 shadow-amber-400/25',
                    step.status === 'current' &&
                      'border-2 border-amber-400 bg-amber-50 text-amber-600 ring-2 shadow-amber-400/20 ring-amber-400/10 dark:bg-amber-950 dark:text-amber-400',
                    step.status === 'upcoming' &&
                      'border-border bg-card text-muted border hover:border-amber-400/30',
                  )}
                >
                  {step.status === 'complete' ? <CheckIcon className="h-4 w-4" /> : index + 1}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium transition-colors duration-200 md:text-sm',
                    'md:whitespace-nowrap',
                    step.status === 'current' && 'font-semibold text-amber-600 dark:text-amber-400',
                    step.status === 'complete' && 'text-foreground',
                    step.status === 'upcoming' && 'text-muted',
                  )}
                >
                  {step.title}
                </span>
              </div>
              {step.description && (
                <p
                  className={cn(
                    'text-center text-xs transition-colors duration-200',
                    step.status === 'current' && 'text-muted',
                    step.status === 'complete' && 'text-muted',
                    step.status === 'upcoming' && 'text-muted',
                  )}
                >
                  {step.description}
                </p>
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'h-px w-2 transition-all duration-500 ease-out sm:w-4 md:w-8',
                  step.status === 'complete'
                    ? 'bg-gradient-to-r from-amber-400 to-amber-300'
                    : 'bg-border',
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
